import { useNavigate } from "react-router";
import { CheckIcon } from "lucide-react";
import editicon from "@/assets/images/edit.svg";
import scriptCall from "@/assets/images/scriptcall.svg";
import onHold from "@/assets/images/onhold.svg";

interface AppointmentDetailsCardProps {
  completedBookingFlow: boolean;
  availableSlotsUrl: string;
  bookingUpdated: boolean;
  bookingCreated: boolean;
  isPaidBooking: boolean;
  isBookingOnHold: boolean;
  isPrepaidPaymentCompleted: boolean;
  customerDetails: any;
  petDetails: any;
  bookingFlowTitle: string;
  bookingFlowSubtitle: string;
  selectedServiceTypeName: string;
  selectedSubService: any;
  slotTimeLabel: string;
  appointmentDateLabel: string;
  selectedSlotStaffDetails: any;
  selectedSlotsClinicDetails: any;
  normalizedUpsellScript: string;
  logPreviewEvent: (event_type: string, extra?: any) => void;
}

const AppointmentDetailsCard = ({
  completedBookingFlow,
  availableSlotsUrl,
  bookingUpdated,
  bookingCreated,
  isPaidBooking,
  isBookingOnHold,
  isPrepaidPaymentCompleted,
  customerDetails,
  petDetails,
  bookingFlowTitle,
  bookingFlowSubtitle,
  selectedServiceTypeName,
  selectedSubService,
  slotTimeLabel,
  appointmentDateLabel,
  selectedSlotStaffDetails,
  selectedSlotsClinicDetails,
  normalizedUpsellScript,
  logPreviewEvent,
}: AppointmentDetailsCardProps) => {
  const navigate = useNavigate();

  return (
    <section className="rounded-[10px] border border-[#E4E7ED] bg-white p-3 shadow-[0_2px_10px_rgba(17,24,39,0.05)]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold leading-6 text-[#1F2431]">
          {completedBookingFlow ? "Appointment Booking Details" : "Preview Appointment Details"}
        </h3>
        {!completedBookingFlow && (
          <button
            type="button"
            onClick={() => {
              logPreviewEvent("CHANGE_SLOT_CLICK", { cta_text: "Edit Slots" });
              navigate(availableSlotsUrl);
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#DEE3EC] bg-white px-[12px] py-[8px] text-[14px] font-medium text-[#0068FF]"
          >
            <img src={editicon} alt="Edit" className="h-3.5 w-3.5" />
            Edit Slots
          </button>
        )}
      </div>
      {completedBookingFlow &&
        (bookingUpdated || bookingCreated) &&
        (isPaidBooking && !isBookingOnHold ? (
          <div
            className={`items-center rounded-[10px] p-[8px] text-[14px] font-semibold mb-2 ${isPrepaidPaymentCompleted
              ? "border border-[#C0FECA] bg-[#EDFFEE] text-[#005303]"
              : "border border-[#FEE1C0] bg-[#FFFBED] text-[#533200]"
              }`}
          >
            <div className="flex gap-2 items-center">
              {isPrepaidPaymentCompleted ? (
                <CheckIcon className="h-4 w-4 text-[#015203]" />
              ) : (
                <img src={onHold} className="h-4 w-4" />
              )}
              {isPrepaidPaymentCompleted
                ? "Booking Created Successfully"
                : bookingUpdated
                  ? "Updated booking is on hold, payment not received yet."
                  : "Booking on hold - Payment not received yet."}
            </div>
            {!isPrepaidPaymentCompleted && (
              <p className="text-[12px] font-medium text-[#000000]">
                This slot is reserved for 15 min. Complete payment to confirm booking or the slot will be released.
              </p>
            )}
            {isPrepaidPaymentCompleted && (
              <p className="text-[12px] font-medium text-[#000000]">
                Appointment has been confirmed. Payment Collected. Confirmation sent to phone number +91{" "}
                {customerDetails?.mobile || "NA"}.
              </p>
            )}
          </div>
        ) : (
          <></>
        ))}
      {completedBookingFlow && !isPaidBooking && (
        <section className="mb-3 rounded-[10px] border border-[#C0FECA] bg-[#EDFFEE] px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-2">
              <CheckIcon className="h-5 w-5 text-[#015203]" />
              <div>
                <h3 className="text-[14px] font-semibold text-[#005303]">{bookingFlowTitle}</h3>
                <p className="text-[12px] font-medium text-[#000000]">{bookingFlowSubtitle}</p>
              </div>
            </div>
          </div>
        </section>
      )}
      <div className="overflow-hidden rounded-[10px] bg-[#EDEDED] p-[1px]">
        <div className="hidden grid-cols-[1.1fr_1fr_1fr_1fr_1fr_1.2fr] gap-4 bg-[#EDEDED] px-4 py-3 text-[12px] font-medium text-[#24282F] md:grid">
          <p>Parent</p>
          <p>Pet Details</p>
          <p>Service</p>
          <p>Time</p>
          <p>Provider</p>
          <p>Clinic</p>
        </div>
        <div className="m-[2px] grid grid-cols-1 gap-4 rounded-[10px] bg-white px-[6px] py-[10px] text-[12px] text-[#232932] md:grid-cols-[1.1fr_1fr_1fr_1fr_1fr_1.2fr]">
          <div>
            <p className="font-medium text-[#2B2E34]">{customerDetails?.name || "NA"}</p>
          </div>
          <div>
            <p className="font-medium text-[#2B2E34]">{petDetails?.name || "NA"}</p>
            <p className="mt-1 text-[#6C727F]">{petDetails?.pet_species_type_id === "1" ? "Canine" : "Feline"}</p>
          </div>
          <div>
            <p className="font-medium text-[#2B2E34]">{selectedServiceTypeName || "NA"}</p>
            <p className="mt-1 text-[#6F7685]">{selectedSubService?.sub_service_name || "No Sub Service"}</p>
          </div>
          <div>
            <p className="font-medium text-[#2B2E34]">{slotTimeLabel}</p>
            <p className="mt-1 text-[#6F7685]">{appointmentDateLabel}</p>
          </div>
          <div>
            <p className="font-medium text-[#2B2E34]">{selectedSlotStaffDetails?.staff_name || "NA"}</p>
            <p className="mt-1 text-[#6F7685]">{selectedSlotStaffDetails?.experience}</p>
          </div>
          <div>
            <p className="font-medium text-[#2B2E34]">{selectedSlotsClinicDetails?.clinic_name || "NA"}</p>
          </div>
        </div>
      </div>

      {!completedBookingFlow && normalizedUpsellScript.length > 0 && (
        <div className="bg-[#FFFBED] font-medium text-[#A06100] mt-2 rounded-[10px]  p-[8px]">
          <div className="flex gap-2 items-center mb-2">
            <img src={scriptCall} alt="Script Call" className="w-3 h-3" />
            <p className=" text-[12px] text-[#533200] font-medium">Script - Read to Parent</p>
          </div>
          <p className="whitespace-pre-wrap text-[12px] text-[#A06100] font-medium">{normalizedUpsellScript}</p>
        </div>
      )}
    </section>
  );
};

export default AppointmentDetailsCard;
